import { API_URL } from '../config/constants';
import axios from 'axios';
import { getStoredAuthToken } from '../utils/auth';

export interface Book {
  id: number;
  title: string;
  author: string;
  description: string;
  category: string;
  pageCount: number | null;
  publishedYear: number | null;
  tags: string;
  isPublic: boolean;
  coverImage: string | null;
  fileType: string;
  createdAt: string;
  updatedAt: string;
}

export interface Collection {
  id: number;
  name: string;
  description: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  books?: Book[];
  bookCount?: number;
}

interface CollectionsResponse {
  collections: Collection[];
}

interface CollectionResponse {
  collection: Collection;
  message?: string;
}

interface MessageResponse {
  message: string;
}

class CollectionService {
  private token: string | null;

  constructor() {
    this.token = getStoredAuthToken();
  }

  private getHeaders() {
    return {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
    };
  }

  // Actualizează token-ul (important pentru operațiuni care necesită autentificare)
  public updateToken() {
    this.token = getStoredAuthToken();
  }

  // Obține toate colecțiile
  public async getAllCollections(): Promise<Collection[]> {
    try {
      this.updateToken();
      const response: any = await axios.get(
        `${API_URL}/books/collections/all`,
        this.getHeaders()
      );
      return response.data.collections || [];
    } catch (error) {
      console.error('Eroare la obținerea colecțiilor:', error);
      throw error;
    }
  }

  // Obține o colecție după ID
  public async getCollectionById(id: number): Promise<Collection> {
    try {
      this.updateToken();
      const response: any = await axios.get(
        `${API_URL}/books/collections/${id}`,
        this.getHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Eroare la obținerea colecției cu ID ${id}:`, error);
      throw error;
    }
  }

  // Adaugă o colecție nouă
  public async addCollection(collectionData: Partial<Collection>): Promise<Collection> {
    try {
      this.updateToken();
      const response: any = await axios.post(
        `${API_URL}/books/collections`,
        collectionData,
        this.getHeaders()
      );
      return response.data.collection;
    } catch (error) {
      console.error('Eroare la adăugarea colecției:', error);
      throw error;
    }
  }

  // Actualizează o colecție
  public async updateCollection(id: number, collectionData: Partial<Collection>): Promise<Collection> {
    try {
      this.updateToken();
      const response: any = await axios.put(
        `${API_URL}/books/collections/${id}`,
        collectionData,
        this.getHeaders()
      );
      return response.data.collection;
    } catch (error) {
      console.error(`Eroare la actualizarea colecției cu ID ${id}:`, error);
      throw error;
    }
  }

  // Șterge o colecție
  public async deleteCollection(id: number): Promise<{ message: string }> {
    try {
      this.updateToken();
      const response: any = await axios.delete(
        `${API_URL}/books/collections/${id}`,
        this.getHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Eroare la ștergerea colecției cu ID ${id}:`, error);
      throw error;
    }
  }

  // Adaugă o carte într-o colecție
  public async addBookToCollection(collectionId: number, bookId: number): Promise<{ message: string }> {
    try {
      this.updateToken();
      const response: any = await axios.post(
        `${API_URL}/books/collections/${collectionId}/books/${bookId}`,
        {},
        this.getHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Eroare la adăugarea cărții ${bookId} în colecția ${collectionId}:`, error);
      throw error;
    }
  }

  // Elimină o carte dintr-o colecție
  public async removeBookFromCollection(collectionId: number, bookId: number): Promise<{ message: string }> {
    try {
      this.updateToken();
      const response: any = await axios.delete(
        `${API_URL}/books/collections/${collectionId}/books/${bookId}`,
        this.getHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Eroare la eliminarea cărții ${bookId} din colecția ${collectionId}:`, error);
      throw error;
    }
  }
}

export default new CollectionService(); 
